<template>
    <div
        class="share-link"
        tabindex="0"
        @click="copyShareLink"
        @keydown.enter="copyShareLink"
        @mousedown.prevent
        @mouseover="breakpoint !== 'black-bear' && mouseOverShareIcon"
        @mouseleave="showShareTooltip = false"
        @focus="mouseOverShareIcon"
        @blur="showShareTooltip = false"
    >
        <Tooltip
            v-if="!showLinkCopiedTooltip && showShareTooltip"
            :styles="{
                backgroundColor: brandColors.grayDivider,
                color: brandColors.brandBlack,
            }"
            class="share-link__tooltip"
        >
            Share Question Link
        </Tooltip>
        <Tooltip
            v-if="!showShareTooltip && showLinkCopiedTooltip"
            class="share-link__tooltip"
            :theme="breakpoint === 'black-bear' ? 'leftalign' : undefined"
            :styles="{
                backgroundColor: brandColors.green,
                color: brandColors.brandBlack,
            }"
        >
            Link Copied!
        </Tooltip>
        <Icon type="share" />
    </div>
</template>

<script setup lang="ts">
import { ref, computed, nextTick } from 'vue'
import BrandColors from '@pocketprep/ui-kit/pocketprep-export.module.scss'
import { qotdModule } from '@/store/qotd/module'
import type { Study } from '@pocketprep/types'
import type { ClientBundle, ClientExam } from '@/store/bundle/state'
import { userModule } from '@/store/user/module'
import { copyText } from '@/utils'
import UIKit from '@pocketprep/ui-kit'
import { screenModule } from '@/store/screen/module'
import { analyticsModule } from '@/store/analytics/module'

const {
    Tooltip,
    Icon,
} = UIKit

const props = defineProps<{
    bundle: Study.Class.BundleJSON | ClientBundle
    examMetadata: Study.Class.ExamMetadataJSON | ClientExam
}>()

const brandColors = BrandColors
const showLinkCopiedTooltip = ref(false)
const showShareTooltip = ref(false)
const clientVersion = import.meta.env.VUE_APP_VERSION

const qotdSharingInfo = computed(() => {
    if (!props.bundle || !props.examMetadata) {
        return undefined
    }

    return qotdModule.getters.getQotDSharingInfo({
        bundleId: props.bundle.objectId,
        examGuid: props.examMetadata.examGuid,
        version: props.examMetadata.version,
        userId: currentUser.value?.objectId || '',
        examName: props.examMetadata.nativeAppName,
    })
})

const currentUser = computed(() => {
    return userModule.state.user
})

const breakpoint = computed(() => {
    return screenModule.getters.getBreakpoint()
})

const mouseOverShareIcon = () => {
    if (!showLinkCopiedTooltip.value && breakpoint.value !== 'black-bear') {
        showShareTooltip.value = true
    }
}

const copyShareLink = async () => {
    if (!qotdSharingInfo.value) {
        return
    }

    if (navigator.share as unknown && qotdSharingInfo.value && window.innerWidth < 768) {
        try {
            await navigator.share({
                title: qotdSharingInfo.value.title,
                url: qotdSharingInfo.value.url,
            })
        } catch (err) {
            // noop
        }
        return
    }

    copyText(qotdSharingInfo.value?.url)
    showShareTooltip.value = false
    nextTick(() => {
        showLinkCopiedTooltip.value = true

        setTimeout(() => {
            showLinkCopiedTooltip.value = false
        }, 2000)
    })

    analyticsModule.actions.amplitudeTrack('QotD Shared', {
        bundleId: props.bundle?.objectId,
        client: 'Web Study',
        clientAppId: 'study.pocketprep.com',
        clientVersion: clientVersion,
        examGuid: props.examMetadata?.examGuid,
        organizationId: userModule.getters.getCurrentOrganizationId() || undefined,
        userDomain: userModule.getters.getUserDomain() || undefined,
    })
}
</script>

<style lang="scss" scoped>
.share-link {
    height: 28px;
    color: $gray-divider;
    cursor: pointer;
    user-select: none;
    position: relative;
    outline: none;

    &:last-child {
        margin-right: 0;
    }

    @media (hover: hover) {
        &:hover,
        &:focus {
            color: $banana-bread;
        }
    }

    &:focus::before {
        content: "";
        border: 1px solid $banana-bread;
        width: 32px;
        height: 32px;
        top: -4px;
        left: -7px;
        border-radius: 4px;
        box-sizing: border-box;
        position: absolute;

        @include breakpoint(black-bear) {
            left: -9px;
        }
    }

    &__tooltip {
        position: absolute;
        top: calc(-100% - 8px);
        left: 50%;
    }
}
</style>
